import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  packageTimeEntriesService,
  CreatePackageTimeEntryDto,
  CreateManualPackageTimeEntryDto,
  FinalizePackageTimeEntryDto,
  UpdatePackageTimeEntryDto,
} from '@/services/packageTimeEntriesService';
import type { PackageTimeEntry, PackageTimeEntrySummary } from '@/types';
import {
  getStoredActiveTimeEntries,
  setStoredActiveTimeEntries,
} from '@/lib/activeTimeEntriesStorage';

/**
 * Package Time Entries API Hooks
 * React Query hooks for time entry management on packages
 */

// Query Keys
export const PACKAGE_TIME_ENTRY_KEYS = {
  all: ['package-time-entries'] as const,
  byPackage: (packageId: number) =>
    [...PACKAGE_TIME_ENTRY_KEYS.all, 'package', packageId] as const,
  active: (packageId: number) =>
    [...PACKAGE_TIME_ENTRY_KEYS.all, 'active', packageId] as const,
  activeAll: () => [...PACKAGE_TIME_ENTRY_KEYS.all, 'active-all'] as const,
  summary: (packageId: number) =>
    [...PACKAGE_TIME_ENTRY_KEYS.all, 'summary', packageId] as const,
};

const upsertActiveEntry = (entries: PackageTimeEntry[], entry: PackageTimeEntry) => {
  const nextEntries = entries.filter((current) => current.id !== entry.id);
  nextEntries.push(entry);

  return nextEntries.sort((a, b) => {
    if (a.status === b.status) {
      return a.packageCode.localeCompare(b.packageCode);
    }

    return a.status === 'running' ? -1 : 1;
  });
};

/**
 * Hook para listar apontamentos de horas de um pacote
 */
export const useGetPackageTimeEntries = (packageId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
    queryFn: () => packageTimeEntriesService.getByPackage(packageId),
    enabled: enabled && packageId > 0,
    staleTime: 30 * 1000, // 30 segundos
  });
};

/**
 * Hook para buscar apontamento ativo do usuario logado
 */
export const useGetActiveTimeEntry = (packageId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
    queryFn: () => packageTimeEntriesService.getActive(packageId),
    enabled: enabled && packageId > 0,
    staleTime: 10 * 1000, // 10 segundos - atualiza frequentemente
    refetchInterval: 30 * 1000, // Refetch a cada 30 segundos para manter sincronizado
  });
};

/**
 * Hook para listar todos os apontamentos ativos do usuario logado
 */
export const useGetActiveTimeEntries = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
    queryFn: async () => {
      const entries = await packageTimeEntriesService.getAllActive();

      if (entries.length > 0) {
        return entries;
      }

      const cachedEntries =
        queryClient.getQueryData<PackageTimeEntry[]>(PACKAGE_TIME_ENTRY_KEYS.activeAll()) ?? [];

      if (cachedEntries.length > 0) {
        return cachedEntries;
      }

      return getStoredActiveTimeEntries();
    },
    initialData: getStoredActiveTimeEntries,
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
  });

  useEffect(() => {
    if (!query.isSuccess) {
      return;
    }

    setStoredActiveTimeEntries(query.data);
  }, [query.data, query.isSuccess]);

  return query;
};

/**
 * Hook para buscar resumo de horas do pacote
 */
export const useGetTimeEntrySummary = (packageId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: PACKAGE_TIME_ENTRY_KEYS.summary(packageId),
    queryFn: () => packageTimeEntriesService.getSummary(packageId),
    enabled: enabled && packageId > 0,
    staleTime: 60 * 1000, // 1 minuto
  });
};

/**
 * Hook para iniciar cronometro
 */
export const useStartTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      data,
    }: {
      packageId: number;
      data: CreatePackageTimeEntryDto;
    }) => packageTimeEntriesService.start(packageId, data),
    onSuccess: (entry, { packageId }) => {
      queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), entry);
      queryClient.setQueryData<PackageTimeEntry[]>(
        PACKAGE_TIME_ENTRY_KEYS.activeAll(),
        (current = []) => {
          const nextEntries = upsertActiveEntry(current, entry);
          setStoredActiveTimeEntries(nextEntries);
          return nextEntries;
        }
      );
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Cronômetro iniciado');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar cronômetro');
    },
  });
};

/**
 * Hook para adicionar apontamento manual
 */
export const useCreateManualTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      data,
    }: {
      packageId: number;
      data: CreateManualPackageTimeEntryDto;
    }) => packageTimeEntriesService.createManual(packageId, data),
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.summary(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Apontamento manual adicionado');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao adicionar apontamento manual');
    },
  });
};

/**
 * Hook para pausar cronometro
 */
export const usePauseTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      entryId,
    }: {
      packageId: number;
      entryId: number;
    }) => packageTimeEntriesService.pause(packageId, entryId),
    onSuccess: (entry, { packageId }) => {
      queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), entry);
      queryClient.setQueryData<PackageTimeEntry[]>(
        PACKAGE_TIME_ENTRY_KEYS.activeAll(),
        (current = []) => {
          const nextEntries = upsertActiveEntry(current, entry);
          setStoredActiveTimeEntries(nextEntries);
          return nextEntries;
        }
      );
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Cronômetro pausado');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao pausar cronômetro');
    },
  });
};

/**
 * Hook para retomar cronometro
 */
export const useResumeTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      entryId,
    }: {
      packageId: number;
      entryId: number;
    }) => packageTimeEntriesService.resume(packageId, entryId),
    onSuccess: (entry, { packageId }) => {
      queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), entry);
      queryClient.setQueryData<PackageTimeEntry[]>(
        PACKAGE_TIME_ENTRY_KEYS.activeAll(),
        (current = []) => {
          const nextEntries = upsertActiveEntry(current, entry);
          setStoredActiveTimeEntries(nextEntries);
          return nextEntries;
        }
      );
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Cronômetro retomado');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao retomar cronômetro');
    },
  });
};

/**
 * Hook para finalizar apontamento
 */
export const useFinalizeTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      entryId,
      data,
    }: {
      packageId: number;
      entryId: number;
      data: FinalizePackageTimeEntryDto;
    }) => packageTimeEntriesService.finalize(packageId, entryId, data),
    onSuccess: (_, { packageId, entryId }) => {
      queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), null);
      queryClient.setQueryData<PackageTimeEntry[]>(
        PACKAGE_TIME_ENTRY_KEYS.activeAll(),
        (current = []) => {
          const nextEntries = current.filter((entry) => entry.id !== entryId);
          setStoredActiveTimeEntries(nextEntries);
          return nextEntries;
        }
      );
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.summary(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Apontamento finalizado com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar apontamento');
    },
  });
};

/**
 * Hook para excluir apontamento
 */
export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      entryId,
    }: {
      packageId: number;
      entryId: number;
    }) => packageTimeEntriesService.delete(packageId, entryId),
    onSuccess: (_, { packageId, entryId }) => {
      queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), null);
      queryClient.setQueryData<PackageTimeEntry[]>(
        PACKAGE_TIME_ENTRY_KEYS.activeAll(),
        (current = []) => {
          const nextEntries = current.filter((entry) => entry.id !== entryId);
          setStoredActiveTimeEntries(nextEntries);
          return nextEntries;
        }
      );
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Apontamento excluído');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir apontamento');
    },
  });
};

/**
 * Hook para editar apontamento
 */
export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      packageId,
      entryId,
      data,
    }: {
      packageId: number;
      entryId: number;
      data: UpdatePackageTimeEntryDto;
    }) => packageTimeEntriesService.update(packageId, entryId, data),
    onSuccess: (entry, { packageId }) => {
      if (entry.status === 'running' || entry.status === 'paused') {
        queryClient.setQueryData(PACKAGE_TIME_ENTRY_KEYS.active(packageId), entry);
        queryClient.setQueryData<PackageTimeEntry[]>(
          PACKAGE_TIME_ENTRY_KEYS.activeAll(),
          (current = []) => {
            const nextEntries = upsertActiveEntry(current, entry);
            setStoredActiveTimeEntries(nextEntries);
            return nextEntries;
          }
        );
      }
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.byPackage(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.active(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.summary(packageId),
      });
      queryClient.invalidateQueries({
        queryKey: PACKAGE_TIME_ENTRY_KEYS.activeAll(),
      });
      toast.success('Apontamento atualizado');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar apontamento');
    },
  });
};
